import { useCallback, useEffect, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import {
  Search,
  Bell,
  HelpCircle,
  ChevronRight,
  ChevronDown,
  ChevronsUpDown,
  CheckCheck,
  X,
  BookOpen,
  MessageCircle,
  Keyboard,
  Sparkles,
  User as UserIcon,
  LogOut,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { GlobalSearch } from "@/components/layout/GlobalSearch"
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts"
import { api, extractErrorMessage, logout } from "@/lib/api"
import { getSocket } from "@/lib/socket"
import { useAuthStore } from "@/stores/auth"
import { useTheme } from "@/contexts/ThemeContext"
import { useToast } from "@/components/use-toast"
import { cn } from "@/lib/utils"

export interface AppHeaderProps {
  title?: string
  breadcrumb?: { label: string; href?: string }[]
}

interface Notification {
  id: string
  type: string
  title: string
  message: string
  link: string | null
  isRead: boolean
  createdAt: string
}

function initials(name: string | undefined): string {
  if (!name) return "?"
  const p = name.trim().split(/\s+/)
  return p.length === 1
    ? p[0].slice(0, 2).toUpperCase()
    : (p[0][0] + p[p.length - 1][0]).toUpperCase()
}

function timeAgo(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return "just now"
  const m = Math.floor(s / 60)
  if (m < 60) return `${m} min ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h} hour${h > 1 ? "s" : ""} ago`
  const d = Math.floor(h / 24)
  return `${d} day${d > 1 ? "s" : ""} ago`
}

const DOT_COLOR: Record<string, string> = {
  SUBMISSION: "bg-blue-500",
  FLAG_RAISED: "bg-red-500",
  GRADING_NEEDED: "bg-amber-500",
  ASSESSMENT_EXPIRING: "bg-amber-500",
  MULTIRATER_RESPONSE: "bg-violet-500",
  SYSTEM: "bg-slate-400",
}

function NotificationBell() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [items, setItems] = useState<Notification[]>([])
  const [unread, setUnread] = useState(0)
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState<"all" | "unread">("all")
  const [pulse, setPulse] = useState(false)
  // Pagination state for the inline "Load more" button. Replaces the old
  // "View all activity → /client/reports" link, which sent users to an
  // unrelated reports page.
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)

  const PAGE_SIZE = 10

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", {
        params: { limit: PAGE_SIZE, offset: 0 },
      })
      const fresh = data.data.items as Notification[]
      setItems(fresh)
      setUnread(data.data.unreadCount as number)
      // Reset pagination on every full reload (mark-all-read, mount, etc).
      setHasMore(fresh.length === PAGE_SIZE)
    } catch {
      /* non-fatal — the bell just stays empty */
    }
  }, [])

  // Append the next page of notifications. Fire-and-forget on the
  // pagination cursor — we use items.length so the request stays in sync
  // with what's already rendered even if the local list was filtered.
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)
    try {
      const { data } = await api.get("/notifications", {
        params: { limit: PAGE_SIZE, offset: items.length },
      })
      const more = data.data.items as Notification[]
      setItems((prev) => [...prev, ...more])
      if (more.length < PAGE_SIZE) setHasMore(false)
    } catch {
      /* swallow — user can retry by clicking again */
    } finally {
      setLoadingMore(false)
    }
  }, [items.length, loadingMore, hasMore])

  useEffect(() => {
    load()
  }, [load])

  // Lightweight badge poll every 30s.
  useEffect(() => {
    const id = window.setInterval(async () => {
      try {
        const { data } = await api.get("/notifications/count")
        setUnread(data.data.unreadCount as number)
      } catch {
        /* ignore */
      }
    }, 30_000)
    return () => window.clearInterval(id)
  }, [])

  // Real-time arrivals via socket.
  useEffect(() => {
    const sock = getSocket()
    const onNew = (n: Notification) => {
      setItems((prev) => [{ ...n, isRead: false }, ...prev].slice(0, 20))
      setUnread((c) => c + 1)
      setPulse(true)
      window.setTimeout(() => setPulse(false), 1500)
      toast(`🔔 ${n.title}`)
    }
    sock.on("notification:new", onNew)
    return () => {
      sock.off("notification:new", onNew)
    }
  }, [toast])

  async function markAll() {
    try {
      await api.put("/notifications/read-all")
      setItems((prev) => prev.map((n) => ({ ...n, isRead: true })))
      setUnread(0)
    } catch (e) {
      toast(extractErrorMessage(e), "error")
    }
  }

  async function openItem(n: Notification) {
    setOpen(false)
    if (!n.isRead) {
      try {
        const { data } = await api.put(`/notifications/${n.id}/read`)
        setUnread(data.data.unreadCount as number)
        setItems((prev) =>
          prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)),
        )
      } catch {
        /* ignore — still navigate */
      }
    }
    if (n.link) navigate(n.link)
  }

  async function remove(e: React.MouseEvent, id: string) {
    e.stopPropagation()
    try {
      const { data } = await api.delete(`/notifications/${id}`)
      setItems((prev) => prev.filter((x) => x.id !== id))
      setUnread(data.data.unreadCount as number)
    } catch (err) {
      toast(extractErrorMessage(err), "error")
    }
  }

  const shown = filter === "unread" ? items.filter((n) => !n.isRead) : items

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative flex h-9 w-9 items-center justify-center rounded-lg text-[color:var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]"
          aria-label="Notifications"
        >
          <Bell className={cn("h-5 w-5", pulse && "animate-bounce")} strokeWidth={1.6} />
          {unread > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unread > 99 ? "99+" : unread}
            </span>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] p-0">
        <div className="flex items-center justify-between px-4 py-3">
          <span className="text-sm font-semibold text-foreground">Notifications</span>
          <button
            onClick={markAll}
            className="inline-flex items-center gap-1 text-xs text-[color:var(--color-accent-hover)] hover:underline"
          >
            <CheckCheck className="h-3.5 w-3.5" /> Mark all
          </button>
        </div>
        <div className="flex gap-1 border-y border-[var(--color-border)] px-3 py-1.5">
          {(["all", "unread"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-medium capitalize",
                filter === f
                  ? "bg-[var(--color-accent-light)] text-[color:var(--color-accent-hover)]"
                  : "text-[color:var(--color-text-muted)] hover:bg-[var(--color-bg-subtle)]",
              )}
            >
              {f === "unread" ? `Unread (${unread})` : "All"}
            </button>
          ))}
        </div>

        <div className="max-h-[360px] overflow-y-auto">
          {shown.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <Bell className="mx-auto h-8 w-8 text-[color:var(--color-text-muted)]" />
              <p className="mt-2 text-sm font-medium text-foreground">
                No notifications yet
              </p>
              <p className="text-xs text-[color:var(--color-text-muted)]">
                You're all caught up! 🎉
              </p>
            </div>
          ) : (
            shown.map((n) => (
              <div
                key={n.id}
                onClick={() => openItem(n)}
                className={cn(
                  "group flex cursor-pointer gap-3 border-b border-[var(--color-border)] px-4 py-3 last:border-0 hover:bg-[var(--color-bg-subtle)]",
                  !n.isRead && "bg-[var(--color-accent-light)]/40",
                )}
              >
                <span
                  className={cn(
                    "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                    DOT_COLOR[n.type] ?? "bg-slate-400",
                  )}
                />
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      "text-sm text-foreground",
                      !n.isRead ? "font-semibold" : "font-normal",
                    )}
                  >
                    {n.title}
                  </p>
                  <p className="truncate text-xs text-[color:var(--color-text-muted)]">
                    {n.message}
                  </p>
                  <p className="mt-0.5 text-[11px] text-[color:var(--color-text-muted)]">
                    {timeAgo(n.createdAt)}
                  </p>
                </div>
                <button
                  onClick={(e) => remove(e, n.id)}
                  className="opacity-0 transition-opacity group-hover:opacity-100"
                  aria-label="Delete notification"
                >
                  <X className="h-3.5 w-3.5 text-[color:var(--color-text-muted)]" />
                </button>
              </div>
            ))
          )}
        </div>
        {hasMore ? (
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="block w-full border-t border-[var(--color-border)] px-4 py-2.5 text-center text-xs font-medium text-[color:var(--color-accent-hover)] hover:bg-[var(--color-bg-subtle)] disabled:opacity-60"
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        ) : items.length > 0 ? (
          <p className="block w-full border-t border-[var(--color-border)] px-4 py-2.5 text-center text-xs text-[color:var(--color-text-muted)]">
            No more notifications
          </p>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const SHORTCUTS: { group: string; rows: [string, string][] }[] = [
  {
    group: "Navigation",
    rows: [
      ["⌘ K", "Open search"],
      ["G then D", "Go to Dashboard"],
      ["G then A", "Go to Assessments"],
      ["G then C", "Go to Candidates"],
      ["G then R", "Go to Reports"],
    ],
  },
  {
    group: "Actions",
    rows: [["⌘ /", "Show shortcuts"]],
  },
]

export function AppHeader({ breadcrumb }: AppHeaderProps) {
  const navigate = useNavigate()
  const { isDark, toggle } = useTheme()
  const user = useAuthStore((s) => s.user)
  const [searchOpen, setSearchOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const isMac = useRef(
    typeof navigator !== "undefined" && /mac/i.test(navigator.platform),
  ).current

  useKeyboardShortcuts({
    onSearch: () => setSearchOpen(true),
    onShortcuts: () => setShortcutsOpen(true),
  })

  async function signOut() {
    await logout()
    navigate("/login", { replace: true })
  }

  return (
    <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b border-[var(--color-border)] bg-[var(--color-bg)] px-6">
      {/* Left: breadcrumb only (each page renders its own PageHeader
          title — the header must not duplicate it). Empty when no
          breadcrumb is passed. */}
      <div className="min-w-0 flex-1">
        {breadcrumb && breadcrumb.length > 0 ? (
          <nav className="flex items-center gap-1.5 text-sm">
            {breadcrumb.map((b, i) => {
              const last = i === breadcrumb.length - 1
              return (
                <span key={i} className="flex items-center gap-1.5">
                  {i > 0 ? (
                    <ChevronRight className="h-3.5 w-3.5 text-[color:var(--color-text-muted)]" />
                  ) : null}
                  {last || !b.href ? (
                    <span
                      className={cn(
                        "truncate",
                        last
                          ? "font-semibold text-foreground"
                          : "text-[color:var(--color-text-muted)]",
                      )}
                    >
                      {b.label}
                    </span>
                  ) : (
                    <Link
                      to={b.href}
                      className="truncate text-[color:var(--color-text-muted)] hover:text-foreground"
                    >
                      {b.label}
                    </Link>
                  )}
                </span>
              )
            })}
          </nav>
        ) : null}
      </div>

      {/* Center: search trigger */}
      <button
        onClick={() => setSearchOpen(true)}
        className="hidden h-9 w-[320px] items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 text-sm text-[color:var(--color-text-muted)] hover:border-[var(--color-border-strong)] md:flex"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Search assessments, candidates…</span>
        <kbd className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-0.5 text-[10px]">
          {isMac ? "⌘" : "Ctrl"}K
        </kbd>
      </button>

      {/* Right: actions */}
      <div className="flex flex-1 items-center justify-end gap-1">
        <button
          onClick={() => setSearchOpen(true)}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-[color:var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] md:hidden"
          aria-label="Search"
        >
          <Search className="h-5 w-5" strokeWidth={1.6} />
        </button>

        <NotificationBell />

        {/* Help */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex h-9 w-9 items-center justify-center rounded-lg text-[color:var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]"
              aria-label="Help"
            >
              <HelpCircle className="h-5 w-5" strokeWidth={1.6} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem
              onClick={() =>
                window.open("https://docs.byldgroup.com", "_blank", "noopener")
              }
            >
              <BookOpen className="mr-2 h-4 w-4" /> Documentation
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/client/support")}>
              <MessageCircle className="mr-2 h-4 w-4" /> Contact Support
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShortcutsOpen(true)}>
              <Keyboard className="mr-2 h-4 w-4" /> Keyboard Shortcuts
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/client/dashboard")}>
              <Sparkles className="mr-2 h-4 w-4" /> What's New
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Profile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ml-1 flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-[var(--color-bg-subtle)]">
              <span
                className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full text-xs font-semibold"
                style={{
                  background: "var(--color-accent-light)",
                  color: "var(--color-accent-hover)",
                }}
              >
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.fullName ?? "Profile"}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  initials(user?.fullName ?? user?.email)
                )}
              </span>
              <span className="hidden max-w-[140px] truncate text-sm font-medium text-foreground lg:block">
                {user?.fullName ?? user?.email}
              </span>
              <ChevronDown className="hidden h-4 w-4 text-[color:var(--color-text-muted)] lg:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="flex items-center gap-3 py-2">
              <span
                className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full text-xs font-semibold"
                style={{
                  background: "var(--color-accent-light)",
                  color: "var(--color-accent-hover)",
                }}
              >
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  initials(user?.fullName ?? user?.email)
                )}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-foreground">
                  {user?.fullName ?? "Client Admin"}
                </span>
                {user?.jobTitle || user?.companyName ? (
                  <span className="block truncate text-xs font-normal text-[color:var(--color-text-secondary)]">
                    {[user?.jobTitle, user?.companyName].filter(Boolean).join(" · ")}
                  </span>
                ) : null}
                <span className="block truncate text-xs font-normal text-[color:var(--color-text-muted)]">
                  {user?.email}
                </span>
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/client/profile")}>
              <UserIcon className="mr-2 h-4 w-4" /> My Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault()
                toggle()
              }}
            >
              <span className="flex flex-1 items-center">
                <ChevronsUpDown className="mr-2 h-4 w-4" /> Dark Mode
              </span>
              <span
                className={cn(
                  "ml-2 inline-flex h-4 w-8 items-center rounded-full px-0.5 transition-colors",
                  isDark ? "bg-[var(--color-accent)]" : "bg-[var(--color-border-strong)]",
                )}
              >
                <span
                  className={cn(
                    "h-3 w-3 rounded-full bg-white transition-transform",
                    isDark && "translate-x-4",
                  )}
                />
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={signOut}
              className="text-[#b91c1c] focus:text-[#b91c1c]"
            >
              <LogOut className="mr-2 h-4 w-4" /> Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      <Dialog open={shortcutsOpen} onOpenChange={setShortcutsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Keyboard Shortcuts</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {SHORTCUTS.map((s) => (
              <div key={s.group}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                  {s.group}
                </p>
                <div className="space-y-1.5">
                  {s.rows.map(([k, label]) => (
                    <div
                      key={k}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-[color:var(--color-text-secondary)]">
                        {label}
                      </span>
                      <kbd className="rounded border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-2 py-0.5 text-xs">
                        {k}
                      </kbd>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </header>
  )
}
